import React, { useEffect, useRef, useState } from 'react';
import './CityAutocompleteInput.css';
import { Search } from 'lucide-react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

const parseFormattedAddress = (formatted) => {
  if (!formatted) return { city: '', state: '', country: '' };

  const [city = '', state = '', country = ''] = String(formatted)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  return { city, state, country };
};

export default function CityAutocompleteInput({
  value,
  onValueChange,
  onSelect,
  inputId = 'city-autocomplete-input',
  placeholder = 'Search city',
}) {
  const places = useMapsLibrary('places');
  const inputRef = useRef(null);
  const listenerRef = useRef(null);
  const selectedDisplayRef = useRef('');
  const onSelectRef = useRef(onSelect);
  const onValueChangeRef = useRef(onValueChange);
  const [draftValue, setDraftValue] = useState(value);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  useEffect(() => {
    onSelectRef.current = onSelect;
    onValueChangeRef.current = onValueChange;
  }, [onSelect, onValueChange]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input || !places) {
      return undefined;
    }

    const autocomplete = new places.Autocomplete(input, {
      fields: ['address_components', 'formatted_address', 'name', 'types'],
      types: ['(cities)'],
    });

    const onPlaceChanged = () => {
      const place = autocomplete.getPlace?.();
      if (!place) return;

      const components = place.address_components || [];
      const getComp = (type) => components.find((comp) => comp.types.includes(type));
      const parsed = parseFormattedAddress(place.formatted_address);

      const city =
        getComp('locality')?.long_name ||
        getComp('postal_town')?.long_name ||
        getComp('administrative_area_level_2')?.long_name ||
        parsed.city ||
        place.name ||
        '';
      const state =
        getComp('administrative_area_level_1')?.short_name ||
        parsed.state ||
        '';
      const countryCode = getComp('country')?.short_name || '';
      const country =
        countryCode === 'US'
          ? 'USA'
          : getComp('country')?.long_name || parsed.country || '';
      const display = [city, state, country].filter(Boolean).join(', ');

      selectedDisplayRef.current = display;
      setDraftValue(display);
      onValueChangeRef.current?.(display, { preserveSelection: true });
      onSelectRef.current?.({
        city,
        state,
        country,
        countryCode,
        display,
      });
    };

    listenerRef.current = autocomplete.addListener('place_changed', onPlaceChanged);

    return () => {
      if (listenerRef.current?.remove) {
        listenerRef.current.remove();
      }
    };
  }, [inputId, places]);

  return (
    <div className="city-autocomplete-container">
      <Search className="city-autocomplete-icon" size={20} aria-hidden="true" />
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        value={draftValue}
        onChange={(event) => {
          setDraftValue(event.target.value);
        }}
        onFocus={(event) => event.target.select()}
        onBlur={(event) => {
          const nextValue = event.target.value;
          const normalizedValue = String(nextValue || '').trim().toLowerCase();
          const normalizedSelected = String(selectedDisplayRef.current || '').trim().toLowerCase();
          const shouldRestoreSelection =
            normalizedSelected &&
            normalizedValue &&
            normalizedSelected.startsWith(normalizedValue) &&
            normalizedSelected.length > normalizedValue.length;

          if (shouldRestoreSelection) {
            setDraftValue(selectedDisplayRef.current);
            onValueChange(selectedDisplayRef.current, { preserveSelection: true });
            return;
          }

          onValueChange(nextValue, { preserveSelection: false });
        }}
        placeholder={placeholder}
        className="city-autocomplete-input"
        autoComplete="off"
        inputMode="text"
      />
    </div>
  );
}
